<template>
  <component
    :is="tag"
    class="bz-list-row"
    :class="{ 'bz-list-row--below': $slots.below, 'bz-list-row--pressable': tag !== 'div', 'bz-list-row--icon': icon, [`bz-list-row--${tone}`]: tone }"
    :for="tag === 'label' ? forId : undefined"
    :type="tag === 'button' ? 'button' : undefined"
    :aria-labelledby="tag === 'button' ? labelId : undefined"
    :aria-describedby="tag === 'button' && hint ? hintId : undefined"
  >
    <BzIcon v-if="icon" :name="icon" class="bz-list-row__icon" />
    <span :id="labelId" class="bz-list-row__label">{{ label }}</span>
    <span v-if="$slots.control" class="bz-list-row__control"><slot name="control" :labelId="labelId" :hintId="hint ? hintId : undefined" /></span>
    <BzIcon v-if="tag === 'button' && !tone" name="chevron_right" class="bz-list-row__chevron" />
    <span v-if="hint" :id="hintId" class="bz-list-row__hint">{{ hint }}</span>
    <span v-if="$slots.below" class="bz-list-row__below"><slot name="below" :labelId="labelId" :hintId="hint ? hintId : undefined" /></span>
  </component>
</template>

<script>
import BzIcon from './BzIcon.vue';

let next = 0;

/**
 * One row of a `BzListGroup`, as iOS draws a setting: an icon, what the row is called and its control at
 * the end of that same line — the chosen value, a switch, a chevron —, and under them a line that says
 * what it does. A slider goes under all of it.
 *
 * @rule   A row that toggles is the `<label>` of its switch (`forId`), so a tap anywhere on it toggles
 *         (norm 9: the whole 72 px row is the target). A row that does something is a `<button>`
 *         (`action`), with a chevron when it opens another screen, and none when it is `tone="danger"`
 *         (sign out: the one destructive row, in the error colour, as Android and iOS draw it).
 *         Anything else is a plain row holding its control.
 * @rule   The control is named by the label alone and described by the hint: the `control` and
 *         `below` slots hand out `labelId` and `hintId`, for its `labelled-by` and `described-by`. A
 *         `<label>` would otherwise name it with its whole text, hint included. A row that is a button
 *         is named and described the same way.
 * @why    72 px: norm 9's height for a primary target, so the row a kitchen taps is never the small one,
 *         and the height of a two-line row on both systems. The label is 18 px: read at 50 cm (norm 9).
 * @why    A press lights the row's background at once and lets it go in 150 ms, as a list row answers
 *         on both systems; never a shrink (App.vue's press scale is for buttons, and a full-width row
 *         that shrinks jumps at both edges).
 * @rule   The control takes at most 45 % of the row: the words are what is read, and a value that does
 *         not fit is cut with an ellipsis rather than squeezing the text into more lines.
 * @why    The hint has a line of its own, the row's full width (Adrián, 14-oct-2026, on a phone): beside the
 *         control it left the name a third of a phone, and the value had been moved under the words to make
 *         room — two lines for what iOS writes in one.
 */
export default {
  name: 'BzListRow',
  components: { BzIcon },
  props: {
    label: { type: String, required: true },
    hint: { type: String, default: undefined },
    icon: { type: String, default: undefined },
    forId: { type: String, default: undefined },
    action: { type: Boolean, default: false },
    tone: { type: String, default: undefined, validator: (t) => ['danger'].includes(t) },
  },
  data() {
    const id = ++next;
    return { labelId: `bz-list-row-${id}-label`, hintId: `bz-list-row-${id}-hint` };
  },
  computed: {
    tag() {
      if (this.action) return 'button';
      return this.forId ? 'label' : 'div';
    },
  },
};
</script>

<style scoped>
.bz-list-row {
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  /* Its lines together in the middle of the 72: left to stretch, the name and the hint split the spare
     height between them and drifted apart. */
  align-content: center;
  gap: 2px var(--bz-space-3);
  box-sizing: border-box;
  width: 100%;
  min-height: var(--bz-target-primary);
  margin: 0;
  padding: var(--bz-space-3) var(--bz-space-4);
  background: transparent;
  border: 0;
  color: var(--bz-ink);
  font-family: var(--bz-font);
  text-align: left;
  /* The browser's text inflation off, so the name and the hint scale together. Left at `auto`, Chrome on
     Android (with a larger system text size) and Safari on an iPhone turned sideways inflate a block by
     how much text it holds: the hint, a sentence, grew ~23 % against its short name and read as big as it
     (Adrián, 28-oct-2026). `100%`, not `none`: zoom and Chrome's own text scaling still apply, to both. */
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
  -webkit-tap-highlight-color: transparent;
  transition: background-color var(--bz-duration-fast) var(--bz-ease-out);
}

/* The hairline between two rows, from where the text starts. */
.bz-list-row + .bz-list-row::before {
  content: '';
  position: absolute;
  top: 0;
  left: var(--bz-space-4);
  right: 0;
  height: 1px;
  background: var(--bz-edge);
  pointer-events: none;
}

.bz-list-row--icon + .bz-list-row--icon::before {
  left: calc(var(--bz-space-4) + var(--bz-icon-md) + var(--bz-space-3));
}

.bz-list-row--pressable {
  cursor: pointer;
}

.bz-list-row--pressable:active {
  background: var(--bz-control);
  transition-duration: 0s;
  transform: none;
  opacity: 1;
}

/* With a keyboard, the system's ring, drawn inside the row so it never leaves the card at its ends
   (cascade.css takes the browser's off every button). */
.bz-list-row--pressable:focus-visible {
  outline: 3px solid var(--bz-focus);
  outline-offset: -3px;
}

.bz-list-row__icon {
  font-size: var(--bz-icon-md);
  color: var(--bz-ink-2);
}

/* A row's title at 500, its hint at 400: told apart by weight as well as by size and ink, so a list of
   settings scans by its names, and the order holds on a screen that draws the two sizes closer than
   asked (Adrián, 28-oct-2026: at 400 the names read no stronger than their hints). It takes what the
   control leaves on its line. */
.bz-list-row__label {
  flex: 1 1 0;
  min-width: 0;
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  font-weight: var(--bz-weight-title);
  color: var(--bz-ink);
}

/* The hint on a line of its own, from where the name starts to the row's end. */
.bz-list-row__hint {
  flex: 1 0 100%;
  box-sizing: border-box;
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink-2);
}

.bz-list-row--icon > .bz-list-row__hint {
  padding-left: calc(var(--bz-icon-md) + var(--bz-space-3));
}

.bz-list-row--danger .bz-list-row__label,
.bz-list-row--danger .bz-list-row__icon {
  color: var(--bz-danger);
}

.bz-list-row__control {
  flex: 0 1 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--bz-space-3);
  min-width: 0;
  max-width: 45%;
}

.bz-list-row__chevron {
  font-size: var(--bz-icon-md);
  color: var(--bz-ink-2);
}

/* A control under the text (the slider): the full width, lined up with the text. */
.bz-list-row__below {
  flex: 1 0 100%;
  box-sizing: border-box;
}

.bz-list-row--icon > .bz-list-row__below {
  padding-left: calc(var(--bz-icon-md) + var(--bz-space-3));
}

/* On a phone the slider takes the row's full width. A value stays on the row's line, as both systems
   draw one, cut with an ellipsis when it does not fit. */
@media (max-width: 599px) {
  .bz-list-row--icon > .bz-list-row__below {
    padding-left: 0;
  }
}
</style>
